/**
 * Utilitários de autenticação
 * Funções para manipulação de JWT e localStorage
 */

import { jwtVerify, SignJWT } from 'jose';
import type { JWTPayload, UserDTO } from '@/types/auth';
import { AUTH_CONSTANTS } from '@/constants/auth';

const secret = new TextEncoder().encode(AUTH_CONSTANTS.JWT_SECRET);

/**
 * Cria um JWT token
 */
export async function createJWT(payload: Omit<JWTPayload, 'iat' | 'exp'>): Promise<string> {
  return new SignJWT(payload)
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()
    .setExpirationTime('24h')
    .sign(secret);
}

/**
 * Verifica e decodifica um JWT token
 */
export async function verifyJWT(token: string): Promise<JWTPayload | null> {
  try {
    const verified = await jwtVerify(token, secret);
    return verified.payload as unknown as JWTPayload;
  } catch (error) {
    console.error('JWT verification failed:', error);
    return null;
  }
}

/**
 * Define um token de autenticação no localStorage
 */
export function setAuthToken(token: string): void {
  if (typeof window !== 'undefined') {
    if (!token) return;
    
    // Ensure we store a clean string, not a JSON stringified one if it already is a string
    let cleanToken = token;
    if (typeof token === 'string' && (token.startsWith('"') && token.endsWith('"'))) {
      cleanToken = token.substring(1, token.length - 1);
    }
    
    localStorage.setItem(AUTH_CONSTANTS.COOKIE_NAME, cleanToken);
  }
}

/**
 * Remove o token de autenticação do localStorage
 */
export function removeAuthToken(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(AUTH_CONSTANTS.COOKIE_NAME);
    localStorage.removeItem('accessToken'); // Compatibility
    localStorage.removeItem('auth_token'); // Compatibility
    localStorage.removeItem('user');
    localStorage.removeItem('user_role');
    // Limpa cache do PatientOnboardingGate para evitar dados antigos no pr\u00f3ximo login
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('checklist_status_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}

/**
 * Obtém o token do localStorage
 */
export function getAuthToken(): string | null {
  if (typeof window !== 'undefined') {
    let token = localStorage.getItem(AUTH_CONSTANTS.COOKIE_NAME) || 
                localStorage.getItem('accessToken') || 
                localStorage.getItem('auth_token');
    
    if (!token || token === 'undefined' || token === 'null') {
      return null;
    }
    
    // Clean token (remove quotes if any)
    if (token.startsWith('"') || token.startsWith("'")) {
      token = token.substring(1, token.length - 1);
    }
    
    return token.trim();
  }
  return null;
}

/**
 * Obtém o usuário autenticado do token
 */
export async function getAuthUser(): Promise<UserDTO | null> {
  const token = getAuthToken();

  if (!token) {
    return null;
  }

  const payload = await verifyJWT(token);

  if (!payload) {
    return null;
  }

  return {
    id: payload.sub,
    email: payload.email,
    name: payload.name,
    role: payload.role,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Valida um email
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Valida requisitos mínimos de senha
 */
export function validatePassword(password: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Senha deve ter no mínimo 8 caracteres');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Senha deve conter uma letra maiúscula');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Senha deve conter uma letra minúscula');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Senha deve conter um número');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
